function asFiniteNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function rangeFor(entries, field) {
    var values = entries.map(function(entry) {
        return asFiniteNumber(entry.item[field]);
    }).filter(function(value) {
        return value !== null;
    });

    if (!values.length) return null;

    return {
        min: Math.min.apply(Math, values),
        max: Math.max.apply(Math, values)
    };
}

export function levelRangeLabel(range) {
    if (!range) return null;
    return range.min === range.max ? String(range.min) : range.min + '–' + range.max;
}

export function sourceElementsLabel(count) {
    var absolute = Math.abs(Number(count) || 0);
    var lastTwo = absolute % 100;
    var last = absolute % 10;
    var noun = absolute === 1
        ? 'element źródłowy'
        : (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 'elementy źródłowe' : 'elementów źródłowych');

    return absolute + ' ' + noun;
}

export function getNearbyFamilyKey(item) {
    var tone = item.negative ? 'danger' : 'success';
    var fallbackLabel = item.group_label || item.category_label || item.layer_label || 'Baza obiektów topograficznych';
    var familyKey = item.family_key || ('label:' + String(fallbackLabel).trim().toLocaleLowerCase('pl'));
    return tone + '::' + familyKey;
}

export function resolveAdjacentFamilyKey(families, activeKey, direction) {
    if (!Array.isArray(families) || !families.length) return null;
    var currentIndex = families.findIndex(function(family) { return family.key === activeKey; });
    if (currentIndex < 0) return null;
    var offset = direction === 'previous' ? -1 : 1;
    var targetIndex = currentIndex + offset;
    if (targetIndex < 0 || targetIndex >= families.length) return null;
    return families[targetIndex].key;
}

export function nearestFamilyEntry(entries) {
    if (!Array.isArray(entries) || !entries.length) return null;
    var withEdgeDistance = entries.filter(function(entry) {
        var distance = Number(entry && entry.item && entry.item.edge_dist_m);
        return Number.isFinite(distance) && distance >= 0;
    });
    var candidates = withEdgeDistance.length ? withEdgeDistance : entries;

    return candidates.reduce(function(best, entry) {
        if (!best) return entry;
        var field = withEdgeDistance.length ? 'edge_dist_m' : 'dist_m';
        var entryDistance = Number(entry && entry.item && entry.item[field]);
        var bestDistance = Number(best && best.item && best.item[field]);
        if (!Number.isFinite(entryDistance)) entryDistance = Number.POSITIVE_INFINITY;
        if (!Number.isFinite(bestDistance)) bestDistance = Number.POSITIVE_INFINITY;
        return entryDistance < bestDistance ? entry : best;
    }, null);
}

export function buildNearbyFamilies(nearbyItems, activeNearbyId) {
    var grouped = {};

    nearbyItems.forEach(function(item, index) {
        var key = getNearbyFamilyKey(item);
        var label = item.family_label || item.group_label || item.category_label || item.layer_label || 'Baza obiektów topograficznych';
        var accent = item.negative ? 'danger' : 'success';

        if (!grouped[key]) {
            grouped[key] = {
                key: key,
                familyKey: item.family_key || null,
                label: label,
                accent: accent,
                count: 0,
                minDist: Number.POSITIVE_INFINITY,
                containsActiveItem: false,
                entries: [],
                components: []
            };
        }

        grouped[key].entries.push({ item: item, index: index });
        grouped[key].count += 1;
        var distance = asFiniteNumber(item.dist_m);
        if (distance !== null && distance < grouped[key].minDist) {
            grouped[key].minDist = distance;
        }
        if (activeNearbyId != null && String(item.bdot10k_id) === String(activeNearbyId)) {
            grouped[key].containsActiveItem = true;
        }
    });

    var families = Object.keys(grouped).map(function(key) {
        var family = grouped[key];
        var componentCounts = {};
        family.entries.sort(function(a, b) {
            var aDist = asFiniteNumber(a.item.dist_m);
            var bDist = asFiniteNumber(b.item.dist_m);
            if (aDist === null) aDist = Number.POSITIVE_INFINITY;
            if (bDist === null) bDist = Number.POSITIVE_INFINITY;
            return aDist - bDist;
        });
        family.entries.forEach(function(entry) {
            var component = entry.item.family_component_label || entry.item.group_label || entry.item.object_label || family.label;
            componentCounts[component] = (componentCounts[component] || 0) + 1;
        });
        family.components = Object.keys(componentCounts).map(function(label) {
            return { label: label, count: componentCounts[label] };
        });
        var componentOrder = { 'Droga': 1, 'Jezdnia': 2, 'Tor kolejowy': 3, 'Teren pod torowiskiem': 4 };
        family.components.sort(function(a, b) {
            var aOrder = componentOrder[a.label] || 99;
            var bOrder = componentOrder[b.label] || 99;
            return aOrder !== bOrder ? aOrder - bOrder : a.label.localeCompare(b.label, 'pl');
        });
        family.nuisanceRange = rangeFor(family.entries, 'nuisance_level');
        family.usefulnessRange = rangeFor(family.entries, 'usefulness_level');
        family.impactRadiusRange = rangeFor(family.entries, 'maximum_impact_radius');
        family.maxImpactRadius = family.impactRadiusRange ? family.impactRadiusRange.max : null;
        family.representative = family.entries.length ? family.entries[0].item : null;

        return family;
    });

    families.sort(function(a, b) {
        var aOrder = a.accent === 'danger' ? 0 : 1;
        var bOrder = b.accent === 'danger' ? 0 : 1;
        if (aOrder !== bOrder) return aOrder - bOrder;
        if (a.minDist !== b.minDist) return a.minDist - b.minDist;
        if (a.count !== b.count) return b.count - a.count;
        return a.label.localeCompare(b.label, 'pl');
    });

    return families;
}
